import { useCallback, useState } from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import {
    SupportTicket,
    SupportTicketCategory,
    SupportTicketListResponse,
    SupportTicketStatus,
    SupportTicketSubCategory,
} from '../models'
import { axiosInstance } from '../helpers'

const SUPPORT_TICKETS_KEY = 'supportTickets' as const

export type SupportTicketStatusFilterValue =
    | 'all'
    | 'active'
    | SupportTicketStatus

export interface SupportTicketListFilters {
    status: SupportTicketStatusFilterValue
    propertyInfo: string
    userSearch: string
    issueSearch: string
    category: SupportTicketCategory | ''
    subCategory: SupportTicketSubCategory | ''
}

export interface SupportTicketListParams {
    page: number
    pageSize: number
}

export const DEFAULT_SUPPORT_TICKET_LIST_FILTERS: SupportTicketListFilters = {
    status: 'active',
    propertyInfo: '',
    userSearch: '',
    issueSearch: '',
    category: '',
    subCategory: '',
}

export const DEFAULT_SUPPORT_TICKET_LIST_PARAMS: SupportTicketListParams = {
    page: 1,
    pageSize: 25,
}

/** Serializes to plain JSON-friendly values for axios + queryKey. */
export function buildSupportTicketListParams(
    filters: SupportTicketListFilters,
    listParams: SupportTicketListParams,
): Record<string, string | number | boolean> {
    const p: Record<string, string | number | boolean> = {
        page: listParams.page,
        page_size: listParams.pageSize,
    }
    if (filters.status === 'active') {
        p.active_only = true
    } else if (filters.status !== 'all') {
        p.status = filters.status
    }
    const property = filters.propertyInfo.trim()
    if (property) p.property_info = property
    const userSearch = filters.userSearch.trim()
    if (userSearch) p.user_search = userSearch
    const issue = filters.issueSearch.trim()
    if (issue) p.issue_search = issue
    if (filters.category) p.category = filters.category
    if (filters.subCategory) p.sub_category = filters.subCategory
    return p
}

function parseSupportTicketListResponse(
    data: unknown,
): SupportTicketListResponse {
    if (Array.isArray(data)) {
        return {
            count: data.length,
            next: null,
            previous: null,
            results: data as SupportTicket[],
        }
    }
    const d = data as Partial<SupportTicketListResponse> | null
    if (d && Array.isArray(d.results)) {
        return {
            count: d.count ?? d.results.length,
            next: d.next ?? null,
            previous: d.previous ?? null,
            results: d.results,
        }
    }
    return { count: 0, next: null, previous: null, results: [] }
}

export interface UseSupportTicketOptions {
    /**
     * When true, fetches the paginated support ticket list. Other callers
     * (e.g. badge, create modal) should omit this to avoid an unnecessary list request.
     */
    listQueryEnabled?: boolean
}

export const useSupportTicket = (options?: UseSupportTicketOptions) => {
    const listQueryEnabled = options?.listQueryEnabled === true
    const queryClient = useQueryClient()

    const [filters, setFilters] = useState<SupportTicketListFilters>({
        ...DEFAULT_SUPPORT_TICKET_LIST_FILTERS,
    })
    const [listParams, setListParams] = useState<SupportTicketListParams>({
        ...DEFAULT_SUPPORT_TICKET_LIST_PARAMS,
    })
    const [pendingTicketCount, setPendingTicketCount] = useState<number>(0)

    const listParamsForQuery = buildSupportTicketListParams(filters, listParams)

    const supportTicketsQuery = useQuery<SupportTicketListResponse>({
        queryKey: [SUPPORT_TICKETS_KEY, listParamsForQuery] as const,
        queryFn: async () => {
            const res = await axiosInstance.get<unknown>('support-ticket/', {
                params: listParamsForQuery,
            })
            return parseSupportTicketListResponse(res.data)
        },
        enabled: listQueryEnabled,
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
    })

    const updateFilters = useCallback(
        (partial: Partial<SupportTicketListFilters>) => {
            setFilters((prev) => ({ ...prev, ...partial }))
            setListParams((p) => ({ ...p, page: 1 }))
        },
        [],
    )

    const updateListParams = useCallback(
        (partial: Partial<SupportTicketListParams>) => {
            setListParams((prev) => {
                const next = { ...prev, ...partial }
                if (partial.pageSize != null && partial.page == null) {
                    next.page = 1
                }
                return next
            })
        },
        [],
    )

    const invalidateSupportTicketList = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: [SUPPORT_TICKETS_KEY] })
    }, [queryClient])

    const createSupportTicketMutation = useMutation(
        (formData: FormData) =>
            axiosInstance.post<SupportTicket>('support-ticket/', formData),
        {
            onSuccess: () => {
                toast.success('Support ticket created successfully')
                invalidateSupportTicketList()
            },
            onError: () => {
                toast.error('Failed to create support ticket')
            },
        },
    )

    const createSupportTicket = useCallback(
        (formData: FormData) => {
            createSupportTicketMutation.mutate(formData)
        },
        [createSupportTicketMutation],
    )

    const updateSupportTicketMutation = useMutation(
        (input: {
            supportTicket: SupportTicket
            status: SupportTicketStatus
        }) =>
            axiosInstance.patch<SupportTicket>(
                `support-ticket/${input.supportTicket.id}/`,
                { status: input.status },
            ),
        {
            onSuccess: () => {
                invalidateSupportTicketList()
            },
            onError: () => {
                toast.error('Failed to update support ticket')
            },
        },
    )

    const updateSupportTicket = useCallback(
        async (supportTicket: SupportTicket, status: SupportTicketStatus) => {
            const response = await updateSupportTicketMutation.mutateAsync({
                supportTicket,
                status,
            })
            return response.data
        },
        [updateSupportTicketMutation],
    )

    const getPendingSupportTicketCount = useCallback(async () => {
        try {
            const response = await axiosInstance.get<number>(
                'support-ticket/get_pending_ticket_count/',
            )
            const count = response.data
            setPendingTicketCount(count)
            return count
        } catch (error) {
            toast.error('Failed to get pending support ticket count')
            return Promise.reject(error)
        }
    }, [])

    const bumpMessageCountForTicket = useCallback(
        (ticketId: number) => {
            queryClient.setQueriesData<SupportTicketListResponse>(
                { queryKey: [SUPPORT_TICKETS_KEY] },
                (old) => {
                    if (!old?.results) return old
                    return {
                        ...old,
                        results: old.results.map((t) =>
                            t.id === ticketId
                                ? {
                                      ...t,
                                      message_count: (t.message_count ?? 0) + 1,
                                  }
                                : t,
                        ),
                    }
                },
            )
        },
        [queryClient],
    )

    const listBodyLoading =
        supportTicketsQuery.isLoading || supportTicketsQuery.isFetching

    return {
        supportTicketList: supportTicketsQuery.data?.results ?? [],
        supportTicketListData: supportTicketsQuery.data,
        listFilters: filters,
        listParams,
        setListFilters: setFilters,
        updateFilters,
        setListParams,
        updateListParams,
        listLoading: supportTicketsQuery.isLoading,
        listFetching: supportTicketsQuery.isFetching,
        /** True during initial load and any list refetch (including filter/page changes). */
        listBodyLoading,
        listError: supportTicketsQuery.isError,
        refetchSupportTicketList: supportTicketsQuery.refetch,
        pendingTicketCount,
        createSupportTicket,
        updateSupportTicket,
        getPendingSupportTicketCount,
        bumpMessageCountForTicket,
    }
}
